import { useCallback, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/api/provider';
import { queryKeys } from '@/api/queryKeys';
import { STALE_TIMES } from '@/api/queryClient';
import { useAccountContext } from '@/app/accounts';
import { Menu, type MenuAnchor, type MenuNode } from '@/features/context-menu';
import { useHandheld } from '@/hooks/useMediaQuery';
import { UNIFIED_LABEL, UNIFIED_PATH } from '@/routes/mail/unifiedRoute';
import { openInNewTab, useRowMenuTriggers } from './FolderMenu';
import { Icon } from './Icon';

/**
 * "All inboxes", at the top of the sidebar's tree (ADR 0056 §7).
 *
 * A place, not a mode: it has a route of its own and it is never the default
 * until someone asks for it in Settings → General. It is drawn only when more
 * than one mailbox is connected — with one, there is nothing to unify and an
 * entry that duplicates Inbox would be noise, so `MailboxTree` draws neither it
 * nor any mailbox row in that case.
 *
 * It is a `treeitem` of the mailbox tree, and a leaf: the tree it sits in is
 * "mailboxes and their folders", and this row is one of the places that tree
 * can take you. (It could not be one while the tree was a single account's
 * folders, which is why it used to sit outside.)
 *
 * Its context menu (right-click, Shift+F10, long-press — the same triggers and
 * the same `Menu` as a folder row) carries only what means something for a
 * merged list: open it in a new tab, refresh it, and mark every inbox read.
 * It is not a folder, so it has nothing to rename, delete, empty or nest.
 */
export function UnifiedFolderEntry({
  selected,
  onSelect,
}: {
  selected: boolean;
  onSelect: () => void;
}) {
  const handheld = useHandheld();
  const variant = handheld ? 'sheet' : 'popover';
  const rowRef = useRef<HTMLDivElement>(null);
  const [request, setRequest] = useState<{ anchor: MenuAnchor; returnFocusTo: HTMLElement | null } | null>(
    null,
  );
  const [status, setStatus] = useState<string | null>(null);
  const { refresh, markAllRead } = useUnifiedInboxActions(setStatus);

  useRowMenuTriggers(
    rowRef,
    true,
    useCallback((anchor: MenuAnchor, returnFocusTo: HTMLElement | null) => {
      setRequest({ anchor, returnFocusTo });
    }, []),
  );

  const items = useMemo<MenuNode[]>(
    () => [
      ...(variant === 'popover'
        ? [
            {
              kind: 'item' as const,
              id: 'open-tab',
              label: 'Open in new tab',
              onSelect: () => openInNewTab(UNIFIED_PATH),
            },
          ]
        : []),
      { kind: 'item', id: 'refresh', label: 'Refresh', onSelect: refresh },
      { kind: 'separator', id: 'sep-read' },
      { kind: 'item', id: 'mark-read', label: 'Mark all inboxes as read', onSelect: markAllRead },
    ],
    [markAllRead, refresh, variant],
  );

  return (
    <div
      ref={rowRef}
      role="treeitem"
      aria-label="All inboxes"
      aria-level={1}
      aria-selected={selected}
      className="unified-entry"
    >
      <div
        className={
          'folder-tree__row' + (selected ? ' is-active' : '') + (request !== null ? ' has-menu' : '')
        }
      >
        <span className="folder-tree__twisty folder-tree__twisty--placeholder" />
        <button
          type="button"
          className="folder-tree__main"
          aria-current={selected ? 'page' : undefined}
          onClick={onSelect}
        >
          <Icon name="inbox" size={16} />
          <span className="folder-tree__label">{UNIFIED_LABEL}</span>
        </button>
      </div>
      {status !== null && (
        <p className="muted folder-tree__status" role="status">
          {status}
        </p>
      )}
      {request !== null && (
        <Menu
          label={`Actions for ${UNIFIED_LABEL}`}
          items={items}
          anchor={request.anchor}
          returnFocusTo={request.returnFocusTo}
          onClose={() => setRequest(null)}
          variant={variant}
        />
      )}
    </div>
  );
}

/**
 * Refresh and mark-read for every mailbox's Inbox at once.
 *
 * Mark-read is one `POST …/mark-read` per mailbox, against the Inbox the
 * folder list names — read from the cache when the sidebar or the merged list
 * already has it, fetched when not. A mailbox that fails does not stop the
 * others, and the result line says how many did not answer rather than
 * reporting "done" over mail that is still unread.
 */
function useUnifiedInboxActions(report: (message: string | null) => void): {
  refresh: () => void;
  markAllRead: () => void;
} {
  const api = useApi();
  const queryClient = useQueryClient();
  const { accounts } = useAccountContext();

  const refresh = useCallback(() => {
    report(null);
    void Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.unifiedMessagesScope() }),
      ...accounts.map((account) =>
        queryClient.invalidateQueries({ queryKey: queryKeys.account(account.id).folders() }),
      ),
    ])
      .then(() => report(`${UNIFIED_LABEL} is up to date.`))
      .catch(() => report(`${UNIFIED_LABEL} could not be refreshed.`));
  }, [accounts, queryClient, report]);

  const markAllRead = useCallback(() => {
    report('Marking every inbox read…');
    void Promise.allSettled(
      accounts.map(async (account) => {
        const folders = await queryClient.ensureQueryData({
          queryKey: queryKeys.account(account.id).folders(),
          queryFn: ({ signal }) => api.folders.list(account.id, signal),
          staleTime: STALE_TIMES.metadata,
        });
        const inbox = folders.find((folder) => folder.role === 'inbox');
        if (inbox === undefined) return { updatedCount: 0, partial: false };
        const result = await api.folders.markAllRead(account.id, inbox.id);
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: queryKeys.account(account.id).folders() }),
          queryClient.invalidateQueries({ queryKey: queryKeys.account(account.id).messagesScope() }),
        ]);
        return result;
      }),
    ).then(async (results) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.unifiedMessagesScope() });
      let updated = 0;
      let incomplete = 0;
      for (const result of results) {
        if (result.status === 'rejected') incomplete += 1;
        else {
          updated += result.value.updatedCount;
          if (result.value.partial === true) incomplete += 1;
        }
      }
      const done =
        updated === 0 ? 'Nothing was unread in any inbox.' : `Marked ${updated.toLocaleString()} messages read.`;
      report(
        incomplete === 0
          ? done
          : `${done} ${incomplete === 1 ? 'One mailbox' : `${incomplete} mailboxes`} could not be finished; try again.`,
      );
    });
  }, [accounts, api, queryClient, report]);

  return { refresh, markAllRead };
}
